import React, { useState, useEffect, useRef, Fragment } from "react";
import { UserAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { Button } from "components/buttons";
import { FormTextInput } from "components/form-fields";
import { ListingTable } from "components/listing";
import { Tooltip } from "components/tooltip";
import { ShowHide } from "components/layout";
import { NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { useDebounce } from "hooks/useDebounce";
import { PcnService } from "services/PcnService";
import { PcnStatusCommentModal } from "pages/pcn/admin/settings/PcnStatusCommentModal";

const requiredAction = "Internal";
const pageSize = 50;
const debounceTimeout = 750;
const badRequestCode = 400;
const generalErrorMessage = "An error occurred while loading status comments. Please try again.";
// The "None" status comment is system-managed and must not be editable.
const protectedStatusComment = "none";
const protectedTooltipMessage = "This status comment is system-managed and cannot be modified.";

const defaultModel = {
    id: 0,
    statusComment: ""
};

const sortableHeaders = [
    { headerName: "Status Comment", sortName: "STATUSCOMMENT" }
];

const tableHeaders = ["Status Comment", "Actions"];

export function PcnStatusCommentsTab() {
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);
    const [loading, setLoading] = useState(true);
    const [tableRows, setTableRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pageNumber, setPageNumber] = useState(1);
    const [sortedBy, setSortedBy] = useState(sortableHeaders[0]);
    const [sortedDir, setSortedDir] = useState("A");

    const [showModal, setShowModal] = useState(false);
    const [isModalUpdate, setIsModalUpdate] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedComment, setSelectedComment] = useState(defaultModel);

    const inputSearchRef = useRef(null);

    function onSearchChange(item) {
        setSearchTerm(item.target.value);
        setPageNumber(1);
    }

    function onSort(header, direction) {
        setSortedBy(header);
        setSortedDir(direction);
        setPageNumber(1);
    }

    function onPagingChange(newPageNumber) {
        setPageNumber(newPageNumber);
    }

    function addStatusComment() {
        setIsModalUpdate(false);
        setSelectedComment(defaultModel);
        setShowModal(true);
    }

    function onEditStatusComment(id) {
        setModalLoading(true);
        setIsModalUpdate(true);
        setShowModal(true);

        PcnService.getStatusComment(id)
            .then((details) => {
                setSelectedComment(details);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load the status comment. Please try again.");
                onCloseModal();
            })
            .finally(() => {
                setModalLoading(false);
            });
    }

    function onCloseModal() {
        setShowModal(false);
        setSelectedComment(defaultModel);
        setIsModalUpdate(false);
    }

    function onSaveModal() {
        onCloseModal();
        fetchData();
    }

    function renderEditControl(row) {
        // The "None" comment is system-managed: disable its edit control and explain why.
        // The disabled Button never fires onClick, so it can safely reference the real handler.
        const isProtected = (row.statusComment ?? "").toLowerCase() === protectedStatusComment;

        return (
            <Tooltip content={isProtected ? protectedTooltipMessage : "Edit status comment"}>
                <Button
                    onClick={() => onEditStatusComment(row.id)}
                    icon="edit"
                    iconOnly={true}
                    minimal={true}
                    large={false}
                    disabled={isProtected}
                />
            </Tooltip>
        );
    }

    function fetchData() {
        setLoading(true);

        const searchRequest = {
            requestCount: pageSize,
            pageNumber: pageNumber,
            searchTerm: debouncedSearchTerm,
            sortField: sortedBy.sortName,
            sortDirection: sortedDir
        };

        PcnService.searchStatusComments(searchRequest)
            .then((response) => {
                const rows = (response.data ?? []).map((row) => {
                    return [
                        row.statusComment,
                        <div key={`${row.id}-actions`} className="button-row-small inline-items">
                            {renderEditControl(row)}
                        </div>
                    ];
                });

                setTableRows(rows);
                setTotalRecords(response.totalCount);
            }, (error) => {
                if (error?.response?.status === badRequestCode) {
                    NotificationToaster.show(Intent.WARNING, error.response.data);
                } else {
                    NotificationToaster.show(Intent.DANGER, generalErrorMessage);
                }
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, generalErrorMessage);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    useEffect(() => {
        fetchData();
    }, [debouncedSearchTerm, pageNumber, sortedBy, sortedDir]);

    return (
        <UserAccess
            perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="spacer-bottom">
                        <Button intent="primary" text="Add new status comment" id="add-status-comment" onClick={addStatusComment} loading={loading} />
                    </div>

                    <FormTextInput
                        inputRef={inputSearchRef}
                        placeholder="Search status comments"
                        disabled={loading && inputSearchRef.current !== document?.activeElement}
                        onChange={onSearchChange}
                        value={searchTerm}
                        large
                        icon="search"
                        id="status-comment-search-field"
                        fill
                    />

                    <ListingTable
                        id="listing-table-status-comments"
                        headers={tableHeaders}
                        data={tableRows}
                        totalRecordCount={totalRecords}
                        loadingData={loading}
                        onPagingChange={onPagingChange}
                        startPage={pageNumber}
                        pageSize={pageSize}
                        showPerPage={false}
                        sortable
                        sortableHeaders={sortableHeaders}
                        sortedBy={sortedBy}
                        sortedDir={sortedDir}
                        onSort={onSort}
                        noDataMessage="No status comments to display"
                    />

                    <ShowHide
                        evaluator={showModal}
                        show={(
                            <PcnStatusCommentModal
                                showModal={showModal}
                                modalTitle={isModalUpdate ? "Update Status Comment" : "Add New Status Comment"}
                                isUpdate={isModalUpdate}
                                loading={modalLoading}
                                onSave={onSaveModal}
                                onCloseModal={onCloseModal}
                                statusCommentDetails={selectedComment}
                            />
                        )}
                    />
                </Fragment>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}
